import type {
  AudioAssetRef,
  Marker,
  Project,
  SnapValue,
  TempoEvent,
  TimeSignatureEvent,
} from '@/domain/types';
import { TEMPO_RAMP_SHAPES, type TempoRampShape } from '@/domain/types';
import { signatureMapOf, tempoMapOf } from '@/domain/selectors';
import { SNAP_VALUES } from '@/timing/snap';
import { barBeatToTicks, tempoEventAt, ticksToBarBeat } from '@/timing/tempoMap';
import { newMarkerId } from '@/util/id';
import { defineCommand } from './registry';
import { boolParam, enumParam, idParam, intParam, numberParam, omitted, stringParam } from './params';

function withTempoMap(project: Project, tempoMap: TempoEvent[]): Project {
  return { ...project, transport: { ...project.transport, tempoMap } };
}

/** The tick of the tempo event governing a position. */
function tempoEventTick(project: Project, tick: number): number {
  return tempoEventAt(tempoMapOf(project), tick).event.tick;
}

export const setTempo = defineCommand<{ tick: number; bpm: number }>({
  type: 'transport.setTempo',
  label: (p) =>
    p.tick === 0 ? `Set tempo to ${p.bpm.toFixed(2)} BPM` : `Set ${p.bpm.toFixed(2)} BPM at tick ${p.tick}`,
  parse: (raw) => {
    const bpm = numberParam(raw.bpm, 5, 900);
    if (bpm === null) return null;
    const tick = omitted(raw.tick) ? 0 : intParam(raw.tick, 0, Number.MAX_SAFE_INTEGER);
    if (tick === null) return null;
    return { tick, bpm };
  },
  coalesceKey: (p) => `tempo:${p.tick}`,
  apply: (project, p) => {
    const existing = project.transport.tempoMap;
    const at = existing.find((e) => e.tick === p.tick);
    if (at && at.bpm === p.bpm) return project;
    // An existing event keeps its ramp: editing the number is not a statement
    // about the shape of the segment leading away from it.
    const next = [...existing.filter((e) => e.tick !== p.tick), { ...at, tick: p.tick, bpm: p.bpm }].sort(
      (a, b) => a.tick - b.tick,
    );
    return withTempoMap(project, next);
  },
});

export const removeTempo = defineCommand<{ tick: number }>({
  type: 'transport.removeTempo',
  label: () => 'Remove tempo change',
  parse: (raw) => {
    const tick = intParam(raw.tick, 0, Number.MAX_SAFE_INTEGER);
    return tick === null ? null : { tick };
  },
  apply: (project, p) => {
    const tick = tempoEventTick(project, p.tick);
    // The event at tick 0 is the project's tempo, not a change; every
    // conversion starts from it.
    if (tick === 0) return project;
    const next = project.transport.tempoMap.filter((e) => e.tick !== tick);
    if (next.length === project.transport.tempoMap.length) return project;
    return withTempoMap(project, next);
  },
});

/**
 * Whether the segment leaving an event ramps, and how. Holding drops the
 * shape too, so a segment set back to ramping starts linear again; a shape
 * given without `ramp` is refused rather than read as a ramp by implication.
 */
/**
 * Replaces the whole tempo map in one edit.
 *
 * A map bent to a performance is dozens of events that mean nothing one at a
 * time; setting them one by one would be dozens of undo steps and dozens of
 * intermediate maps that move every clip. Validated event by event exactly as
 * a project file's map is, and the first event is pinned to tick zero.
 */
export const setTempoMap = defineCommand<{ tempoMap: TempoEvent[] }>({
  type: 'transport.setTempoMap',
  label: () => 'Replace the tempo map',
  parse: (raw) => {
    if (!Array.isArray(raw.tempoMap) || raw.tempoMap.length === 0 || raw.tempoMap.length > 10000) return null;
    const tempoMap: TempoEvent[] = [];
    for (const entry of raw.tempoMap) {
      if (typeof entry !== 'object' || entry === null) return null;
      const record = entry as Record<string, unknown>;
      const tick = intParam(record.tick, 0, Number.MAX_SAFE_INTEGER);
      const bpm = numberParam(record.bpm, 5, 900);
      if (tick === null || bpm === null) return null;
      const ramp = record.ramp === undefined ? null : boolParam(record.ramp);
      if (record.ramp !== undefined && ramp === null) return null;
      const shape = record.rampShape === undefined ? null : enumParam(record.rampShape, TEMPO_RAMP_SHAPES);
      if (record.rampShape !== undefined && shape === null) return null;
      tempoMap.push({
        tick,
        bpm,
        ...(ramp ? { ramp: true } : {}),
        ...(ramp && shape === 'exponential' ? { rampShape: shape } : {}),
      });
    }
    tempoMap.sort((a, b) => a.tick - b.tick);
    if (tempoMap[0].tick !== 0) return null;
    return { tempoMap };
  },
  apply: (project, p) => withTempoMap(project, p.tempoMap),
});

export const setTempoRamp = defineCommand<{ tick: number; ramp: boolean; shape: TempoRampShape }>({
  type: 'transport.setTempoRamp',
  label: (p) => (p.ramp ? (p.shape === 'exponential' ? 'Ramp the tempo, evenly' : 'Ramp the tempo') : 'Hold the tempo'),
  parse: (raw) => {
    const tick = intParam(raw.tick, 0, Number.MAX_SAFE_INTEGER);
    const ramp = boolParam(raw.ramp);
    // Missing means straight; a shape this map does not know is refused.
    const shape = raw.shape === undefined ? 'linear' : enumParam(raw.shape, TEMPO_RAMP_SHAPES);
    if (tick === null || ramp === null || !shape) return null;
    return { tick, ramp, shape };
  },
  apply: (project, p) => {
    const tick = tempoEventTick(project, p.tick);
    const existing = project.transport.tempoMap;
    const at = existing.find((e) => e.tick === tick);
    if (!at) return project;
    const currentShape = at.rampShape ?? 'linear';
    if ((at.ramp === true) === p.ramp && (!p.ramp || currentShape === p.shape)) return project;
    const next = existing.map((e) =>
      e.tick === tick
        ? p.ramp
          ? { tick: e.tick, bpm: e.bpm, ramp: true, ...(p.shape === 'exponential' ? { rampShape: p.shape } : {}) }
          : { tick: e.tick, bpm: e.bpm }
        : e,
    );
    return withTempoMap(project, next);
  },
});

/**
 * The start of the bar containing `tick`, under the meter map as it stands.
 *
 * Meter changes are only written on bar lines. A change part-way through a bar
 * would leave a stub bar that the ruler, the bar/beat read-out and the click
 * would each have to explain; snapping the request to the bar the user pointed
 * at is both the conventional behaviour and the one with no edge cases.
 */
function barStartTick(project: Project, tick: number): number {
  const map = signatureMapOf(project);
  return barBeatToTicks(map, ticksToBarBeat(map, tick).bar);
}

function withSignatures(project: Project, timeSignatures: TimeSignatureEvent[]): Project {
  return { ...project, transport: { ...project.transport, timeSignatures } };
}

export const setTimeSignature = defineCommand<{ tick: number; numerator: number; denominator: number }>({
  type: 'transport.setTimeSignature',
  label: (p) =>
    p.tick === 0
      ? `Set time signature to ${p.numerator}/${p.denominator}`
      : `Set ${p.numerator}/${p.denominator} at tick ${p.tick}`,
  parse: (raw) => {
    const numerator = intParam(raw.numerator, 1, 32);
    const denominator = intParam(raw.denominator, 1, 32);
    if (numerator === null || denominator === null) return null;
    // Only power-of-two denominators are musically meaningful here.
    if ((denominator & (denominator - 1)) !== 0) return null;
    const tick = omitted(raw.tick) ? 0 : intParam(raw.tick, 0, Number.MAX_SAFE_INTEGER);
    if (tick === null) return null;
    return { tick, numerator, denominator };
  },
  coalesceKey: (p) => `signature:${p.tick}`,
  apply: (project, p) => {
    const tick = barStartTick(project, p.tick);
    const existing = project.transport.timeSignatures;
    const at = existing.find((e) => e.tick === tick);
    if (at && at.numerator === p.numerator && at.denominator === p.denominator) return project;

    const kept = existing.filter((e) => e.tick !== tick);
    const next = [...kept, { tick, numerator: p.numerator, denominator: p.denominator }].sort(
      (a, b) => a.tick - b.tick,
    );
    return withSignatures(project, next);
  },
});

export const removeTimeSignature = defineCommand<{ tick: number }>({
  type: 'transport.removeTimeSignature',
  label: () => 'Remove time signature change',
  parse: (raw) => {
    const tick = intParam(raw.tick, 0, Number.MAX_SAFE_INTEGER);
    return tick === null ? null : { tick };
  },
  apply: (project, p) => {
    const tick = barStartTick(project, p.tick);
    // The signature at tick 0 is the project's meter, not a change, and every
    // conversion assumes it is there.
    if (tick === 0) return project;
    const next = project.transport.timeSignatures.filter((e) => e.tick !== tick);
    if (next.length === project.transport.timeSignatures.length) return project;
    return withSignatures(project, next);
  },
});

export const setSnap = defineCommand<{ snap: SnapValue }>({
  type: 'transport.setSnap',
  label: (p) => `Set snap to ${p.snap}`,
  parse: (raw) => {
    const snap = enumParam<SnapValue>(raw.snap, SNAP_VALUES);
    return snap ? { snap } : null;
  },
  apply: (project, p) => ({ ...project, transport: { ...project.transport, snap: p.snap } }),
});

export const setLoopRegion = defineCommand<{ startTicks: number; endTicks: number; enabled: boolean | null }>({
  type: 'transport.setLoopRegion',
  label: () => 'Set loop region',
  parse: (raw) => {
    const startTicks = intParam(raw.startTicks, 0, Number.MAX_SAFE_INTEGER);
    const endTicks = intParam(raw.endTicks, 0, Number.MAX_SAFE_INTEGER);
    if (startTicks === null || endTicks === null) return null;
    return { startTicks, endTicks, enabled: raw.enabled === undefined ? null : boolParam(raw.enabled) };
  },
  coalesceKey: () => 'loopRegion',
  apply: (project, p) => {
    const start = Math.min(p.startTicks, p.endTicks);
    const end = Math.max(p.startTicks, p.endTicks);
    if (end - start < 1) return project;
    return {
      ...project,
      transport: {
        ...project.transport,
        loopStartTicks: start,
        loopEndTicks: end,
        loopEnabled: p.enabled ?? project.transport.loopEnabled,
      },
    };
  },
});

export const setLoopEnabled = defineCommand<{ enabled: boolean }>({
  type: 'transport.setLoopEnabled',
  label: (p) => (p.enabled ? 'Enable loop' : 'Disable loop'),
  parse: (raw) => {
    const enabled = boolParam(raw.enabled);
    return enabled === null ? null : { enabled };
  },
  apply: (project, p) => ({ ...project, transport: { ...project.transport, loopEnabled: p.enabled } }),
});

export const setMetronome = defineCommand<{ enabled: boolean }>({
  type: 'transport.setMetronome',
  label: (p) => (p.enabled ? 'Enable metronome' : 'Disable metronome'),
  parse: (raw) => {
    const enabled = boolParam(raw.enabled);
    return enabled === null ? null : { enabled };
  },
  apply: (project, p) => ({ ...project, transport: { ...project.transport, metronomeEnabled: p.enabled } }),
});

export const setCountIn = defineCommand<{ bars: number }>({
  type: 'transport.setCountIn',
  label: (p) => `Set count-in to ${p.bars} bar(s)`,
  parse: (raw) => {
    const bars = intParam(raw.bars, 0, 8);
    return bars === null ? null : { bars };
  },
  apply: (project, p) => ({ ...project, transport: { ...project.transport, countInBars: p.bars } }),
});

export const addMarker = defineCommand<{ markerId: string; tick: number; name: string }>({
  type: 'transport.addMarker',
  label: (p) => `Add marker "${p.name}"`,
  parse: (raw) => {
    const markerId = raw.markerId === undefined ? newMarkerId() : idParam(raw.markerId);
    const tick = intParam(raw.tick, 0, Number.MAX_SAFE_INTEGER);
    if (!markerId || tick === null) return null;
    return { markerId, tick, name: stringParam(raw.name, 128, 'Marker') ?? 'Marker' };
  },
  apply: (project, p) => {
    if (project.markers.some((m) => m.id === p.markerId)) return project;
    const marker: Marker = { id: p.markerId, name: p.name, tick: p.tick, color: '#e0a13c' };
    return { ...project, markers: [...project.markers, marker].sort((a, b) => a.tick - b.tick) };
  },
});

export const removeMarker = defineCommand<{ markerId: string }>({
  type: 'transport.removeMarker',
  label: () => 'Delete marker',
  parse: (raw) => {
    const markerId = idParam(raw.markerId);
    return markerId ? { markerId } : null;
  },
  apply: (project, p) => ({ ...project, markers: project.markers.filter((m) => m.id !== p.markerId) }),
});

export const renameProject = defineCommand<{ name: string }>({
  type: 'project.rename',
  label: (p) => `Rename project to "${p.name}"`,
  parse: (raw) => {
    const name = stringParam(raw.name, 200);
    return name ? { name } : null;
  },
  coalesceKey: () => 'projectName',
  apply: (project, p) => ({ ...project, meta: { ...project.meta, name: p.name } }),
});

/**
 * Registers imported or recorded audio with the project. The bytes themselves
 * are already in storage by the time this runs; the command only records the
 * reference, which is what makes it undoable and serialisable.
 */
export const registerAsset = defineCommand<{ asset: AudioAssetRef }>({
  type: 'asset.register',
  label: (p) => `Import "${p.asset.name}"`,
  parse: (raw) => {
    const asset = raw.asset;
    if (typeof asset !== 'object' || asset === null) return null;
    const a = asset as Record<string, unknown>;
    const id = idParam(a.id);
    const durationSec = numberParam(a.durationSec, 0, 86400);
    const sampleRate = intParam(a.sampleRate, 1, 768000);
    const channelCount = intParam(a.channelCount, 1, 32);
    if (!id || durationSec === null || sampleRate === null || channelCount === null) return null;
    return {
      asset: {
        id,
        name: stringParam(a.name, 256, 'Audio') ?? 'Audio',
        originalFileName: typeof a.originalFileName === 'string' ? stringParam(a.originalFileName, 256) : null,
        mimeType: stringParam(a.mimeType, 128, 'audio/wav') ?? 'audio/wav',
        byteLength: intParam(a.byteLength, 0, Number.MAX_SAFE_INTEGER, 0) ?? 0,
        sampleRate,
        channelCount,
        durationSec,
        createdAt: intParam(a.createdAt, 0, Number.MAX_SAFE_INTEGER, Date.now()) ?? Date.now(),
        recorded: boolParam(a.recorded, false) ?? false,
      },
    };
  },
  apply: (project, p) => {
    if (project.assets[p.asset.id]) return project;
    return { ...project, assets: { ...project.assets, [p.asset.id]: p.asset } };
  },
});
